import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { gql } from "../../../__generated__";
import type {
  AdminStoresForManagerCreateQuery,
  AdminCreateManagerMutation,
  AdminCreateManagerMutationVariables,
} from "../../../__generated__/graphql";
import { notify, notifyError } from "../../../utils/toast";
import { AdminListDetailLayout } from "../components/admin-list-detail-layout";
import { AdminStoreTableWithMobileCards } from "../components/admin-store-table-with-mobile-cards";
import { useScrollToDetailPanelOnSelect } from "../hooks/use-scroll-to-detail-panel";

const ADMIN_STORES_FOR_MANAGER_CREATE = gql(`
  query AdminStoresForManagerCreate {
    adminStoresForManagerCreate {
      id
      title
      address
      managerId
      manager {
        id
        name
      }
    }
  }
`);

const ADMIN_CREATE_MANAGER = gql(`
  mutation AdminCreateManager($input: AdminCreateManagerInput!) {
    adminCreateManager(input: $input) {
      ok
      error
      managerId
    }
  }
`);

type StoreRow = NonNullable<
  AdminStoresForManagerCreateQuery["adminStoresForManagerCreate"]
>[number];

export default function AdminManagerCreate() {
  const [selectedStoreIds, setSelectedStoreIds] = useState<number[]>([]);
  const [name, setName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [password, setPassword] = useState("");

  useScrollToDetailPanelOnSelect(
    selectedStoreIds.length > 0 ? { id: selectedStoreIds[0] } : null
  );

  const { data, loading, error, refetch } = useQuery<AdminStoresForManagerCreateQuery>(
    ADMIN_STORES_FOR_MANAGER_CREATE
  );
  const [adminCreateManager, { loading: creating }] = useMutation<
    AdminCreateManagerMutation,
    AdminCreateManagerMutationVariables
  >(ADMIN_CREATE_MANAGER, {
    onCompleted: (res) => {
      if (res.adminCreateManager.ok) {
        notify("매니저 계정이 생성되었습니다.");
        setSelectedStoreIds([]);
        setName("");
        setContactNumber("");
        setPassword("");
        refetch();
      } else {
        notifyError(res.adminCreateManager.error ?? "생성에 실패했습니다.");
      }
    },
    onError: (e) => {
      notifyError(e.message);
    },
  });

  const stores: StoreRow[] = data?.adminStoresForManagerCreate ?? [];

  const handleStoreToggle = (storeId: number) => {
    const store = stores.find((s) => s.id === storeId);
    if (store?.managerId != null) return;
    setSelectedStoreIds((prev) =>
      prev.includes(storeId) ? prev.filter((id) => id !== storeId) : [...prev, storeId]
    );
  };

  const handleSubmit = async () => {
    if (selectedStoreIds.length < 2) {
      notifyError("2개 이상의 매장을 선택해주세요.");
      return;
    }
    const hasAssigned = stores.some(
      (s) => selectedStoreIds.includes(s.id) && s.managerId != null
    );
    if (hasAssigned) {
      notifyError("이미 매니저가 할당된 매장이 선택되어 있습니다.");
      return;
    }
    if (!name.trim()) {
      notifyError("이름을 입력해주세요.");
      return;
    }
    if (!contactNumber.trim()) {
      notifyError("연락처를 입력해주세요.");
      return;
    }
    if (!password.trim()) {
      notifyError("비밀번호를 입력해주세요.");
      return;
    }
    if (
      !window.confirm(
        `선택한 ${selectedStoreIds.length}개 매장에 매니저 계정을 생성하시겠습니까?`
      )
    ) {
      return;
    }
    await adminCreateManager({
      variables: {
        input: {
          name: name.trim(),
          contactNumber: contactNumber.trim(),
          password: password.trim(),
          storeIds: selectedStoreIds,
        },
      },
    });
  };

  const canSubmit =
    selectedStoreIds.length >= 2 &&
    name.trim().length > 0 &&
    contactNumber.trim().length > 0 &&
    password.trim().length > 0 &&
    !creating;

  if (loading) {
    return (
      <div className="w-full p-4">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">에러: {error.message}</div>
      </div>
    );
  }

  return (
    <AdminListDetailLayout
      title="매니저 계정 생성"
      description="입점된 매장 중 매니저가 할당되지 않은 매장만 선택할 수 있습니다. 2개 이상 선택 후 매니저 정보를 입력하세요."
      leftPanel={{
        title: "입점 매장 목록",
        children: (
          <AdminStoreTableWithMobileCards
            stores={stores.map((s) => ({
              id: s.id,
              title: s.title,
              address: s.address ?? undefined,
            }))}
            selectionMode="multiple"
            selectedIds={selectedStoreIds}
            onSelect={(store) => handleStoreToggle(store.id)}
            showImage={false}
            disabledStoreIds={stores
              .filter((s) => s.managerId != null)
              .map((s) => s.id)}
            extraHeaders={
              <th className="px-4 py-2 font-medium text-gray-700">
                매니저
              </th>
            }
            renderExtraCells={(store) => {
              const full = stores.find((x) => x.id === store.id);
              if (!full) return null;
              return (
                <td
                  className="px-4 py-3 text-gray-600 text-xs"
                  title={
                    full.managerId != null
                      ? "이미 매니저가 할당된 매장입니다"
                      : undefined
                  }
                >
                  {full.manager ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-200 text-gray-700">
                      매니저: {full.manager.name}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
              );
            }}
            renderExtraCard={(store) => {
              const full = stores.find((x) => x.id === store.id);
              if (!full) return null;
              return (
                <p className="text-xs text-gray-600 mt-1">
                  {full.manager
                    ? `매니저: ${full.manager.name}`
                    : "매니저 없음"}
                </p>
              );
            }}
            emptyText="입점된 매장이 없습니다."
          />
        ),
      }}
      rightPanel={{
        title: "매니저 정보",
        emptyText: "2개 이상의 매장을 선택해주세요.",
        children:
          selectedStoreIds.length >= 2 ? (
            <div className="space-y-4">
              <div className="text-sm text-gray-700">
                <span className="font-medium">선택된 매장:</span>{" "}
                {selectedStoreIds.length}개
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  이름
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="매니저"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  연락처
                </label>
                <input
                  type="text"
                  value={contactNumber}
                  onChange={(e) => setContactNumber(e.target.value)}
                  placeholder="연락처 번호"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  비밀번호
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="비밀번호 (연락처와 동일하게 설정 가능)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="pt-2">
                <button
                  type="button"
                  disabled={!canSubmit}
                  onClick={handleSubmit}
                  className="w-full px-4 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creating ? "생성 중..." : "매니저 계정 생성"}
                </button>
              </div>
            </div>
          ) : selectedStoreIds.length === 1 ? (
            <p className="text-sm text-amber-600">
              2개 이상 선택해주세요. (현재 1개)
            </p>
          ) : null,
      }}
    />
  );
}
